import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { FadeUp, MaskedReveal, SectionLabel } from "@/components/Motion";
import { ArrowUpRight, ArrowLeft, Share2, Copy, Download } from "lucide-react";
import { QUESTIONS, PRIMARY_COPY, SECONDARY_COPY, MIX_COPY, COLOUR_TOKENS } from "./copy";
import { score } from "./scoring";
import { downloadStoryCard, getStoryCardObjectUrl } from "./storyCard";

const STORAGE_KEY = "safemama.cmm.answers";

function useAnswers() {
  const [answers, setAnswers] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
  });
  const save = (a) => { setAnswers(a); sessionStorage.setItem(STORAGE_KEY, JSON.stringify(a)); };
  return [answers, save];
}

/* ---------------- Landing ---------------- */
export function Landing() {
  const nav = useNavigate();
  return (
    <div className="pt-10 md:pt-16" data-testid="cmm-landing">
      <section className="editorial-container">
        <SectionLabel number="No. 008" label="Colour Me Mama" />
        <h1 className="mt-6 font-serif text-[13vw] sm:text-[9vw] md:text-[7.5vw] lg:text-[7rem] leading-[1] tracking-tight text-ink pb-4">
          <MaskedReveal text="Colour me" as="span" className="block" delay={0.1} animate />
          <MaskedReveal text="mama!" as="span" className="block italic text-terracotta" delay={0.35} animate />
        </h1>
        <FadeUp delay={0.8}>
          <p className="mt-8 font-serif text-2xl md:text-3xl leading-[1.35] text-ink italic max-w-3xl">
            Every mama has a colour. Find out yours.
          </p>
          <p className="mt-8 font-serif text-lg md:text-xl leading-relaxed text-ink/75 max-w-2xl">
            16 little questions. No right answers. No wrong answers. Just choose what feels most like you.
          </p>
          <div className="mt-12 flex items-center gap-6 flex-wrap">
            <button onClick={() => { sessionStorage.removeItem(STORAGE_KEY); nav("/colour-me-mama/quiz/1"); }} className="btn-pill" data-testid="cmm-start">
              Find my colour <ArrowUpRight size={14} />
            </button>
            <span className="overline text-ink/50">About 3 minutes.</span>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}

/* ---------------- Quiz ---------------- */
export function Quiz({ index }) {
  const nav = useNavigate();
  const [answers, save] = useAnswers();
  const i = Math.max(1, Math.min(16, Number(index) || 1));
  const q = QUESTIONS[i - 1];
  const selected = answers[i];

  const choose = (letter) => {
    const next = { ...answers, [i]: letter };
    save(next);
    setTimeout(() => {
      if (i >= 16) nav("/colour-me-mama/finding");
      else nav(`/colour-me-mama/quiz/${i + 1}`);
    }, 180);
  };

  return (
    <div className="pt-10 md:pt-16 pb-20" data-testid="cmm-quiz">
      <section className="editorial-container max-w-3xl">
        <div className="flex items-center justify-between">
          <button
            onClick={() => (i > 1 ? nav(`/colour-me-mama/quiz/${i - 1}`) : nav("/colour-me-mama"))}
            className="overline text-ink/60 hover:text-ink transition-colors flex items-center gap-2"
            data-testid="cmm-back"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="overline text-ink/60" data-testid="cmm-progress">{i} of 16</div>
        </div>

        <div className="mt-6 h-px bg-ink/15 relative overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${(i / 16) * 100}%` }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="h-full bg-terracotta" />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="mt-12">
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-ink">{q.q}</h2>
            {q.note && <p className="mt-4 font-serif italic text-ink/60 text-lg">{q.note}</p>}

            <div className="mt-10 space-y-4">
              {["A","B","C","D"].map((L) => (
                <button
                  key={L}
                  onClick={() => choose(L)}
                  className={`w-full text-left border ${selected === L ? "border-ink bg-ink/5" : "border-ink/20 hover:border-ink/60"} px-6 py-5 md:px-8 md:py-6 rounded-sm transition-colors`}
                  data-testid={`cmm-choice-${L}`}
                >
                  <div className="flex items-baseline gap-5">
                    <span className="overline text-ink/50 w-4 shrink-0">{L}</span>
                    <span className="font-serif text-lg md:text-xl text-ink leading-relaxed">{q.choices[L]}</span>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </section>
    </div>
  );
}

/* ---------------- Finding ---------------- */
export function Finding() {
  const nav = useNavigate();
  useEffect(() => {
    const t = setTimeout(() => nav("/colour-me-mama/result"), 1600);
    return () => clearTimeout(t);
  }, [nav]);
  return (
    <div className="pt-24 pb-24 min-h-[60vh] flex items-center" data-testid="cmm-finding">
      <div className="editorial-container text-center">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="overline text-ink/50">Colour Me Mama</div>
          <h2 className="mt-6 font-serif italic text-5xl md:text-7xl text-terracotta">Finding your colour…</h2>
          <p className="mt-6 font-serif text-xl text-ink/70 italic">Putting your shades together.</p>
        </motion.div>
      </div>
    </div>
  );
}

/* ---------------- Result ---------------- */
function useResult() {
  const [answers] = useAnswers();
  return useMemo(() => {
    const answered = Object.keys(answers).length;
    if (answered < 16) return null;
    return score(answers);
  }, [answers]);
}

export function Result() {
  const nav = useNavigate();
  const r = useResult();
  useEffect(() => { if (!r) nav("/colour-me-mama"); }, [r, nav]);
  if (!r) return null;
  const P = COLOUR_TOKENS[r.primary];
  const S = COLOUR_TOKENS[r.secondary];
  const primary = PRIMARY_COPY[r.primary];
  const secondary = SECONDARY_COPY[r.secondary];
  const mix = MIX_COPY[`${r.primary}+${r.secondary}`];

  return (
    <div className="pt-10 md:pt-16 pb-20" data-testid="cmm-result">
      <section className="editorial-container">
        <SectionLabel number="No. 008" label="Your Mama Colour" />
        <div className="mt-8 flex items-center gap-4 flex-wrap">
          <span className="w-14 h-14 rounded-full" style={{ background: P.hex }} />
          <span className="w-10 h-10 rounded-full" style={{ background: S.hex }} />
        </div>
        <h1 className="mt-8 font-serif text-[11vw] sm:text-[7vw] md:text-[6vw] lg:text-[5.5rem] leading-[1.02] tracking-tight pb-4">
          <span className="block text-ink">You're a</span>
          <span className="block italic" style={{ color: P.hex }} data-testid="cmm-primary">{P.label} mama</span>
          <span className="block text-ink text-3xl md:text-5xl italic mt-4">with a touch of <span style={{ color: S.hex }} data-testid="cmm-secondary">{S.label}</span></span>
        </h1>
      </section>

      <section className="mt-16 editorial-container max-w-3xl">
        <div className="overline text-ink/50">{P.emoji} {P.label} · Primary</div>
        <p className="mt-2 font-serif italic text-xl text-ink/70">{P.tagline}</p>
        <div className="mt-10 font-serif text-xl leading-[1.6] text-ink/85 space-y-6 whitespace-pre-line">{primary.body}</div>

        <div className="mt-16 border-t border-ink/15 pt-10">
          <div className="overline text-ink/50">{primary.strengthTitle}</div>
          <div className="mt-4 font-serif text-2xl md:text-3xl italic leading-[1.3]" style={{ color: P.hex }}>{primary.strength.split("\n\n")[0]}</div>
          {primary.strength.split("\n\n")[1] && (
            <p className="mt-4 font-serif text-lg text-ink/70 leading-relaxed">{primary.strength.split("\n\n").slice(1).join("\n\n")}</p>
          )}
        </div>

        <div className="mt-16 border-t border-ink/15 pt-10">
          <div className="overline text-ink/50">{S.emoji} A touch of {S.label}</div>
          <p className="mt-4 font-serif text-xl text-ink/80 leading-[1.55]">{secondary}</p>
        </div>

        <div className="mt-16 border-t border-ink/15 pt-10">
          <div className="overline text-ink/50">Your Colour Mix</div>
          <p className="mt-4 font-serif italic text-2xl md:text-3xl text-ink leading-[1.3]">{mix}</p>
        </div>

        <div className="mt-14">
          <Link to="/colour-me-mama/share" className="btn-pill" data-testid="cmm-share-cta">Share result <ArrowUpRight size={14} /></Link>
        </div>
      </section>

      {/* Inline "Meet The Safe Mama Editorial" — the previous separate Meet page,
          now a natural scroll-continuation of the result. */}
      <section className="mt-24 md:mt-32 editorial-container max-w-3xl" data-testid="cmm-meet-inline">
        <div className="border-t border-ink/15 pt-14">
          <SectionLabel number="No. 008" label="Meet Safe Mama" />
          <h2 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-ink pb-4">
            <span className="block">Now you know your colour.</span>
            <span className="block italic text-terracotta">Meet The Safe Mama Editorial.</span>
          </h2>
          <div className="mt-10 font-serif text-xl md:text-2xl leading-[1.5] text-ink/85 space-y-6">
            <p><em className="italic">Motherhood doesn't look the same for everyone. That's rather the point.</em></p>
            <p>The Safe Mama Editorial is a monthly motherhood mail club, delivered to your doorstep. Each edition arrives as a thoughtfully curated envelope filled with real letters from mothers, nutrition-led meal plans and recipes, expert guidance, and little discoveries for the stage of motherhood you're in.</p>
            <p>Choose the edition that feels like you — <em className="italic">Almost Mama</em> for pregnancy or <em className="italic">Mama</em> for motherhood — and receive it every month, or simply order a single edition to see what arrives in the mail.</p>
          </div>
          <div className="mt-12">
            <Link to="/mail-club" className="btn-pill" data-testid="cmm-discover">Discover The Safe Mama Editorial <ArrowUpRight size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------------- Share ---------------- */
export function Share() {
  const r = useResult();
  const nav = useNavigate();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!r) nav("/colour-me-mama"); }, [r, nav]);
  useEffect(() => {
    let cancelled = false;
    let urlToRevoke = null;
    if (r) {
      (async () => {
        try {
          const u = await getStoryCardObjectUrl(r.primary, r.secondary);
          if (cancelled) { URL.revokeObjectURL(u); return; }
          urlToRevoke = u;
          setPreviewUrl(u);
        } catch { /* preview optional */ }
      })();
    }
    return () => { cancelled = true; if (urlToRevoke) URL.revokeObjectURL(urlToRevoke); };
  }, [r]);
  if (!r) return null;
  const P = COLOUR_TOKENS[r.primary]; const S = COLOUR_TOKENS[r.secondary];
  const url = `${window.location.origin}/colour-me-mama`;
  const text = `Colour Me Mama! I'm a ${P.label} mama with a touch of ${S.label}. Every mama has a colour. Find out yours. — The Safe Mama Editorial`;

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Colour Me Mama", text, url }); return; } catch {}
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Result copied to your clipboard.");
    } catch {
      toast.error("Couldn't copy — long-press and copy manually.");
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {
      toast.error("Couldn't copy — long-press and copy manually.");
    }
  };
  const download = async () => {
    setBusy(true);
    try {
      await downloadStoryCard(r.primary, r.secondary);
      toast.success("Story image downloaded.");
    } catch (e) {
      toast.error("Couldn't create the image. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-10 md:pt-16 pb-20" data-testid="cmm-share">
      <section className="editorial-container max-w-3xl">
        <SectionLabel number="No. 008" label="Share your result" />

        <div className="mt-8 font-serif text-lg md:text-xl text-ink/75 max-w-xl leading-relaxed">
          A little postcard for your feed. Download the image for Instagram Stories, or share the link so a friend can find her colour too.
        </div>

        {previewUrl ? (
          <div className="mt-10 flex justify-center">
            <img
              src={previewUrl}
              alt={`${P.label} mama story card`}
              className="w-full max-w-[360px] rounded-md shadow-[0_30px_80px_-30px_rgba(46,38,32,0.35)]"
              data-testid="cmm-share-preview"
            />
          </div>
        ) : (
          <div className="mt-10 relative rounded-md overflow-hidden shadow-[0_30px_80px_-30px_rgba(46,38,32,0.35)]" style={{ background: P.hex, color: P.ink }}>
            <div className="p-10 md:p-14">
              <div className="overline opacity-70">Colour Me Mama!</div>
              <div className="mt-8 font-serif italic text-4xl md:text-6xl leading-[1.02]">I'm a {P.label} mama</div>
              <div className="mt-3 font-serif text-2xl md:text-3xl italic opacity-85">with a touch of <span style={{ background: S.hex, color: S.ink, padding: "0 8px", borderRadius: 999 }}>{S.label}</span></div>
              <div className="mt-14 border-t border-white/20 pt-6 flex items-center justify-between">
                <div className="opacity-75 font-serif italic">Every mama has a colour. Find out yours.</div>
                <div className="overline opacity-80">The Safe Mama Editorial</div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-4">
          <button onClick={download} disabled={busy} className="btn-pill" data-testid="cmm-share-download">
            <Download size={14} /> {busy ? "Preparing…" : "Download story image"}
          </button>
          <button onClick={share} className="btn-pill ghost" data-testid="cmm-share-native"><Share2 size={14} /> Share result</button>
          <button onClick={copy} className="btn-pill ghost" data-testid="cmm-share-copy"><Copy size={14} /> Copy quiz link</button>
        </div>

        <p className="mt-6 overline text-ink/50">Image size: 1080 × 1920 · perfect for Instagram Stories.</p>
      </section>
    </div>
  );
}

/* ---------------- Meet Safe Mama (final page) ---------------- */
export function Meet() {
  return (
    <div className="pt-10 md:pt-16 pb-20" data-testid="cmm-meet">
      <section className="editorial-container max-w-3xl">
        <SectionLabel number="No. 008" label="Meet Safe Mama" />
        <h1 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-ink pb-4">
          <span className="block">Now you know your colour.</span>
          <span className="block italic text-terracotta">Meet Safe Mama.</span>
        </h1>
        <div className="mt-10 font-serif text-xl md:text-2xl leading-[1.5] text-ink/85 space-y-6">
          <p><em className="italic">Motherhood doesn't look the same for everyone. That's rather the point.</em></p>
          <p>The Safe Mama Editorial is a thoughtful monthly edit for the motherhood you're actually living — real letters from mothers, nutrition and recipes, expert guidance and little discoveries designed to make this stage feel a little more understood.</p>
        </div>
        <div className="mt-12">
          <Link to="/mail-club" className="btn-pill" data-testid="cmm-discover">Discover The Safe Mama Editorial <ArrowUpRight size={14} /></Link>
        </div>
      </section>
    </div>
  );
}
